import React from 'react';
import { useTranslation } from 'react-i18next';
import { OrganizationsIcon } from '@common/utils/icons/icons';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import RobotInformation from '../../models/robots/robot-information';

const MetaSeparator: React.FC = () => (
  <Box
    aria-hidden
    component="span"
    sx={{
      width: 3,
      height: 3,
      flexShrink: 0,
      borderRadius: '50%',
      backgroundColor: 'text.disabled',
    }}
  />
);

const RobotPageTitle: React.FC<{
  robot: RobotInformation | null;
  pageName?: string;
  isUpdating?: boolean;
  isLoading?: boolean;
  hasFailed?: boolean;
}> = ({ robot, pageName, isUpdating, isLoading, hasFailed = false }) => {
  const { t } = useTranslation();

  const tags = robot?.tags ?? [];
  const hasMeta =
    isLoading ||
    robot?.organizationName != null ||
    robot?.serialNumber != null ||
    tags.length > 0 ||
    isUpdating === true;

  return (
    <Stack spacing={0.5}>
      <Stack direction="row" alignItems="baseline" columnGap={1} flexWrap="wrap" useFlexGap>
        {isLoading ? (
          <Skeleton variant="text" width={180} />
        ) : (
          <Typography variant="h2" color={robot == null ? 'text.secondary' : undefined}>
            {robot?.name ?? (hasFailed ? t('error-loading-robot') : t('error-robot-not-found'))}
          </Typography>
        )}
        {pageName && !isLoading && (
          <React.Fragment>
            <Typography aria-hidden variant="h2" color="text.disabled" fontWeight={300}>
              /
            </Typography>
            <Typography variant="h2" color="text.secondary" fontWeight={400}>
              {pageName}
            </Typography>
          </React.Fragment>
        )}
      </Stack>

      {hasMeta && (
        <Stack
          direction="row"
          alignItems="center"
          columnGap={1}
          rowGap={0.5}
          flexWrap="wrap"
          useFlexGap
        >
          {isLoading ? (
            <Skeleton variant="text" width={150} />
          ) : (
            <React.Fragment>
              {robot?.organizationName && (
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <OrganizationsIcon color="action" sx={{ fontSize: 16 }} />
                  <Typography variant="body2" color="text.secondary">
                    {robot.organizationName}
                  </Typography>
                </Stack>
              )}

              {robot?.organizationName && robot?.serialNumber && <MetaSeparator />}

              {robot?.serialNumber && (
                <Typography variant="body2" color="text.secondary" sx={{ letterSpacing: 0.3 }}>
                  {robot.serialNumber}
                </Typography>
              )}

              {tags.length > 0 && (robot?.organizationName || robot?.serialNumber) && (
                <MetaSeparator />
              )}

              {tags.map((tag) => (
                <Chip key={tag.id} label={tag.tag} size="small" variant="outlined" />
              ))}

              {isUpdating && (
                <Chip
                  size="small"
                  color="info"
                  variant="outlined"
                  icon={<CircularProgress size={12} color="inherit" />}
                  label={t('chip-torizon-updating')}
                />
              )}
            </React.Fragment>
          )}
        </Stack>
      )}
    </Stack>
  );
};

export default RobotPageTitle;
